import * as Yup from 'yup';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';
import { Stack, TextField, IconButton, InputAdornment } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { makeStyles } from '@material-ui/core';
import { toast } from 'react-toastify';
import EyeOpen from '../../../assets/images/eyeon.svg';
import EyeClose from '../../../assets/images/eyeoff.svg';
// import classNames from 'classnames';
import http from '../../../config/http';
import { AUTH_POST_LOGIN_URL } from '../../../config/api';
import { _route } from '../../../utils';

const useStyles = makeStyles(() => ({
  textField: {
    padding: '0px!important',
    fontSize: '16px!important',
    fontFamily: 'Inter !important',
    fontWeight: '400',
    marginTop: '8px!important',
    transition: '.3s',
    '& .MuiInputLabel-formControl': {
      fontSize: '14px',
      lineHeight: '1em',
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      // border: '1px solid #5433FF',
    },
    '& .MuiFormHelperText-root': {
      letterSpacing: '0 !important',
      fontFamily: 'Inter',
      margin: '6px 0 0 0 !important',
      fontWeight: '500',
    },
    '& .MuiOutlinedInput-root .MuiInputBase-input': {
      borderRadius: '8px',
      background: '#fff',
      fontSize: '14px !important',
      // border: '1px solid #D0D5DD',
      boxShadow: ' 0px 1px 2px rgb(16 24 40 / 5%)',
      padding: '13.5px 14px',
      transition: '.3s',
      '&:focus': {
        transition: '.3s',
        outline: '0',
        boxShadow: 'inset 0 1px 1px rgb(0 0 0 / 8%), 0 0 8px rgb(102 175 233 / 60%)',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderWidth: '1px !important',
    },
  },
}));

const buttonStyles = {
  borderRadius: '10px',
  boxShadow: '0 4px 11px rgb(0 0 0 / 11%)',
  width: '100%',
  padding: '10px 14px',
  color: '#fff',
  transition: '0.2s',
  marginTop: '5px',
  fontWeight: '500',
  backgroundColor: '#5433ff',
  borderColor: '#5433ff',
  border: '1px solid #5433ff',
  fontFamily: 'Inter',
  letterSpacing: '0',
  textTransform: 'inherit',
  '&:hover': {
    backgroundColor: '#5433FF',
    borderColor: '#5433FF',
    opacity: '0.9',
    boxShadow: '0 4px 11px rgb(0 0 0 / 11%)',
  },
  '&:focus': {
    backgroundColor: '#5433FF',
    borderColor: '#5433FF',
    opacity: '0.9',
    boxShadow: '0 4px 11px rgb(0 0 0 / 11%)',
  },
  '&:visited': {
    backgroundColor: '#5433FF',
    borderColor: '#5433FF',
    opacity: '0.9',
    boxShadow: '0 4px 11px rgb(0 0 0 / 11%)',
  },
};

export default function LoginForm() {
  const [showPassword, setShowPassword] = useState(false);
  const classes = useStyles();
  const LoginSchema = Yup.object().shape({
    email: Yup.string().email('Email must be a valid email address').required('Email is required'),
    password: Yup.string().required('Password is required'),
  });

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      remember: true,
    },
    validationSchema: LoginSchema,
    onSubmit: async (e, { setSubmitting }) => {
      try {
        const response = await http.post(AUTH_POST_LOGIN_URL, { email: values.email, password: values.password });
        let href = _route('stores');
        if (response.data.stores.length === 0) {
          href = _route('growz.overview');
        } else if (response.data.stores.length === 1) {
          href = _route('growz');
        }
        window.location.href = href;
      } catch (err) {
        toast.error(err.response.data.message);
      }
      setSubmitting(false);
    },
  });

  const { errors, touched, values, isSubmitting, handleSubmit, getFieldProps } = formik;

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <div className="customLabel">Email*</div>
          <TextField
            fullWidth
            autoComplete="email"
            type="email"
            {...getFieldProps('email')}
            error={Boolean(touched.email && errors.email)}
            helperText={touched.email && errors.email}
            className={classes.textField}
            placeholder="Enter your email"
            InputLabelProps={{ shrink: false }}
            // value={emailValue}
            // onChange={(e) => setemailValue(e.target.value)}
            // variant="outlined"
          />

          <div className="customLabel mgTop">Password*</div>
          <TextField
            fullWidth
            autoComplete="password"
            type={showPassword ? 'text' : 'password'}
            // label="Password"
            className={classes.textField}
            {...getFieldProps('password')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleShowPassword} edge="end">
                    {showPassword ? <img src={EyeOpen} /> : <img src={EyeClose} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            error={Boolean(touched.password && errors.password)}
            helperText={touched.password && errors.password}
            placeholder="Enter your password"
            InputLabelProps={{ shrink: false }}
          />
        </Stack>

        <Stack direction="row" alignItems="end" justifyContent="end" sx={{ my: 2 }}>
          {/* <FormControlLabel
            control={<Checkbox {...getFieldProps('remember')} checked={values.remember} />}
            label="Remember me"
          /> */}

          <Link variant="subtitle2" to="/password/reset" className="forgotPass" underline="hover">
            Forgot your password?
          </Link>
        </Stack>

        <LoadingButton
          sx={buttonStyles}
          size="large"
          fullWidth
          type="submit"
          className={classes.authSubmitBtn}
          variant="contained"
          loading={isSubmitting}
        >
          Log in
        </LoadingButton>
      </Form>
    </FormikProvider>
  );
}
